/* Outreach sequences: the cold half of Momentum.
 *
 * A sequence is a TYPE (what it is for), a GOAL, and three TOUCHES — a warm
 * opening, a value-give with a soft mention of a free offer, and an open-door
 * close. EACH TOUCH HAS ITS OWN CHANNEL: a meeting reminder can open on email and
 * close with a text. That is the redesign she specced 2026-09-15
 * (docs/outreach-sequences-redesign-spec.md), built 2026-09-16; before it, one
 * platform was chosen for the whole sequence and every touch inherited it.
 *
 * `outbound_sequences` / `outbound_messages` have existed since supabase/0001
 * with nothing ever reading them. supabase/0009 adds the four columns this needs.
 * The TABLES keep their `outbound_` names while the nav reads "Outreach
 * Sequences" — ids never move, only labels.
 *
 * Everything above `useOutreach` is pure, so the derived states this screen leans
 * on are testable without a Supabase round trip. See outreach.test.js.
 */

import { useCallback, useMemo } from "react";
import { createRecordStore } from "./recordStore";
import { offerLine } from "./offerLine";
import { reorderWrites } from "./reorder";
import { rungLabel } from "./offerRung";
import { applyLabelledBlock, ordinalWord, parseLabelledBlock, stripLabelledBlock } from "./sectionBlock";

/* ── What a sequence is for ──────────────────────────────────────────────────
   Asked FIRST, on Add — before any touch exists (her spec, point 1). Stored in
   `outbound_sequences.type`, added by supabase/0019. A sequence made before that
   has no type and says so ("Set type") rather than guessing one. */
/* `goal` is what the type is FOR, written in as a starting point when she adds
   one (her call, 2026-09-17: "goal should probably be pre filled right?").
   Hers to change — it is her words Brill reads, and a goal she has edited is
   never overwritten. A type with no goal would leave the field blank, which is
   what it did before. */
/* EACH TYPE HAS A STAGE (her call, 2026-10-01): one set of stage names in
   Offers, Outreach and Scripts — Trust Building · Core · Continuation.
   Sales and Meeting Follow-up earn the yes (Trust Building; Meeting Follow-up
   defaults there because discovery calls are the common case). CORE PROGRAM is
   new: touches during her main coaching engagement — her words, "the core of a
   coaching business is the coaching … the first big push", and "onboarding"
   alone named only its first stretch. Customer Success moved to Continuation
   ("customer success is actually continuation"); none had been saved, so
   nothing moved with it. A sequence LINKED to an offer takes the offer's stage
   instead (sequenceStage) — one answer, never two. */
export const SEQUENCE_TYPES = [
  { id: "outbound_sales", stage: "trust", label: "Outbound Sales Outreach", hint: "Cold or warm prospecting sequences",
    goal: "Start a real conversation with someone who has never heard from me" },
  { id: "inbound_sales", stage: "trust", label: "Inbound Sales", hint: "Nurture for leads who've already raised a hand",
    goal: "Turn interest into a booked call" },
  { id: "meeting_follow_up", stage: "trust", label: "Meeting Follow-up", hint: "Reminders, confirmations, no-show recovery",
    goal: "Get them to show up to the meeting, and back on the calendar if they don't" },
  { id: "core_program", stage: "core", label: "Core Program", hint: "Touches during your main coaching program — onboarding, check-ins, between sessions",
    goal: "Get a new client started well and through the first big push" },
  { id: "customer_success", stage: "continuation", label: "Customer Success", hint: "Check-ins and retention, after the main program",
    goal: "Keep a client going after the main transformation" },
];

/* The three stages in order, for the Add menu's headers and the table's bands. */
export const SEQUENCE_STAGES = ["trust", "core", "continuation"];

/* What Brill is told about the stage — the same answer the table bands by,
   in her words, so a Core Program touch is written to a client and never
   sold to (api/_lib/prompts.js outreach_copy). */
const STAGE_MEANS = {
  trust: "earning the yes — they are not a client yet",
  core: "a client inside her main coaching program — they have already said yes",
  continuation: "a client after the main program — keeping them going",
};
export function stageLine(sequence, offers = []) {
  const s = sequenceStage(sequence, offers);
  return `Stage: ${s ? `${rungLabel(s)} — ${STAGE_MEANS[s]}` : "not set"}`;
}

/** Which stage a sequence is in: its linked offer's, else its type's, else none. */
export function sequenceStage(sequence, offers = []) {
  const id = sequence?.offerId;
  const offer = id ? (offers || []).find((o) => o.id === id) : null;
  const fromOffer = ["trust", "core", "continuation"].includes(offer?.type) ? offer.type : null;
  return fromOffer || sequenceTypeDef(sequence?.type)?.stage || null;
}

/** The type, or null for a sequence that has none yet. */
export function sequenceTypeDef(id) {
  return SEQUENCE_TYPES.find((t) => t.id === id) || null;
}

/* ── Channels, one per TOUCH ─────────────────────────────────────────────────
   The limit is GUIDANCE, never enforcement — the same rule the website's field
   guides follow. Nothing truncates and nothing blocks a save; going over turns
   the counter, because the place that limit bites is the channel's own compose
   box, and finding out there is worse than finding out here.

   Email has no character limit worth counting against, so it counts WORDS
   instead — and it is the only channel with a subject line.

   TEXT / SMS is new with the redesign. 160 characters is ONE text; longer is
   sent as several, which is the thing worth knowing while writing one. Nothing
   in Brill sends a text — there is no sending at all yet — so this is a length
   guide and nothing more (the spec's open question about a provider stays
   open). `short` is the badge in the table's Channels column. */
export const CHANNELS = [
  { id: "email", label: "Email", short: "EM", limit: null, subject: true },
  { id: "text", label: "Text / SMS", short: "TX", limit: 160, subject: false, over: "Longer than one text. It still sends, as several — trim it if you want it to land as one." },
  { id: "linkedin", label: "LinkedIn", short: "LI", limit: 1300, subject: false },
  { id: "instagram", label: "Instagram", short: "IG", limit: 1000, subject: false },
  { id: "facebook", label: "Facebook", short: "FB", limit: 2000, subject: false },
];

/* A touch with no channel yet. New touches start here: which channel a touch
   goes out on is hers to choose, and the spec leaves "should Brill suggest one
   per type" open, so nothing is guessed. */
export const NO_CHANNEL = { id: "", label: "Choose channel", short: "", limit: null, subject: false };

/** The channel a touch goes out on. Old rows written as `linkedin_dm` (0001's
 *  comment) still read as LinkedIn; anything unknown reads as unchosen. */
export function channelDef(id) {
  const key = id === "linkedin_dm" ? "linkedin" : id;
  return CHANNELS.find((c) => c.id === key) || NO_CHANNEL;
}

/** The channels a sequence actually uses, in touch order, each once. */
export function sequenceChannels(touches = []) {
  const seen = [];
  for (const t of touches || []) {
    const c = channelDef(t?.channel);
    if (c.id && !seen.includes(c)) seen.push(c);
  }
  return seen;
}

/* ── The touches ─────────────────────────────────────────────────────────────
   A sequence is AS MANY TOUCHES AS SHE NEEDS — her call, 2026-09-16: "a 7
   message sequence is pretty normal." It used to be exactly three, fixed roles.

   The arc still holds: the FIRST touch is always the warm opening and the LAST
   is always the open-door close. Everything between is hers to add, remove and
   reorder, and each one says what it does from the MIDDLE roles below (she
   approved the list 2026-09-16). New sequences still start with three.

   `empty` is the coaching copy a touch shows — role-specific, because a generic
   "no copy yet" teaches her nothing about what this touch is for. `cta` is the
   hint under the offer picker, which differs sharply by role. */
export const TOUCH_ROLES = [
  {
    id: "opening",
    label: "Warm opening",
    empty: "A warm opening names something specific and real — a post, a comment, a mutual connection. Not a template.",
    cta: "Rarely mentioned this early — leave it blank unless it comes up naturally.",
  },
  {
    id: "value",
    label: "Value give",
    empty: "The value-give gives something useful before it asks for anything, with one soft mention of your free offer.",
    cta: "The soft mention lives here. Pulled from your Offer Ladder.",
  },
  {
    id: "followup",
    label: "Follow-up",
    empty: "A light check-in that picks up the thread. It asks for nothing new.",
    cta: "Usually none — a follow-up that pitches reads as pressure.",
  },
  {
    id: "reminder",
    label: "Reminder",
    empty: "A reminder of what's coming — the time, the place, what to bring. Short and useful.",
    cta: "Only if the reminder is for this offer.",
  },
  {
    id: "story",
    label: "Story or proof",
    empty: "Your own story or a real result — something true that shows why this matters. Never an invented client.",
    cta: "Optional — the story can point at the offer it's about.",
  },
  {
    id: "invitation",
    label: "Invitation",
    empty: "The clear ask, made once — what you'd like them to do next, and an easy way to say yes or no.",
    cta: "The offer this invitation is for.",
  },
  {
    id: "close",
    label: "Open-door close",
    empty: "The close leaves the door open without pressure. No follow-up guilt, no last chance.",
    cta: "Optional — most closes don't repeat the offer.",
  },
];

export const OPENING_ROLE = "opening";
export const CLOSE_ROLE = "close";
/* What a touch between the opening and the close can be. */
export const MIDDLE_ROLES = TOUCH_ROLES.filter((r) => r.id !== OPENING_ROLE && r.id !== CLOSE_ROLE);
/* What a new sequence starts with. */
export const STARTING_ROLES = [OPENING_ROLE, "value", CLOSE_ROLE];
/* Kept for the tests and anything that still asks for "the three". */
export const TOUCH_ROLE_ORDER = STARTING_ROLES;

export function roleDef(id) {
  return TOUCH_ROLES.find((r) => r.id === id) || TOUCH_ROLES[1];
}

/* A role-less legacy guess at position, for a touch read on its own. */
const LEGACY_POSITION = { opening: 1, value: 2, close: 3 };

/** "Touch 4 · Follow-up". The number is the touch's PLACE in its sequence
 *  (`position`, set by `useOutreach` when it lists them), so it reads the same
 *  everywhere the touch is shown. */
export function touchLabel(touch) {
  const n = touch?.position || LEGACY_POSITION[touch?.touchRole] || 1;
  return `Touch ${n} · ${roleDef(touch?.touchRole || OPENING_ROLE).label}`;
}

/** Can this touch be moved or removed? The opening and the close are the arc's
 *  two ends and stay put. */
export function isMiddleTouch(touch) {
  return touch?.touchRole !== OPENING_ROLE && touch?.touchRole !== CLOSE_ROLE;
}

/**
 * The rows to write when a middle touch is dragged. Pure, so it can be tested.
 * The opening stays first and the close stays last whatever the drag says: a
 * drop onto either end lands just inside it. The list is renumbered 0..n-1.
 */
export function touchReorderWrites(touches = [], from, to) {
  const list = [...(touches || [])];
  if (from < 0 || from >= list.length || !isMiddleTouch(list[from])) return [];
  const firstMiddle = list.findIndex(isMiddleTouch);
  const lastMiddle = list.length - 1 - [...list].reverse().findIndex(isMiddleTouch);
  const target = Math.max(firstMiddle, Math.min(lastMiddle, to));
  if (target === from) return [];
  const [moved] = list.splice(from, 1);
  list.splice(target, 0, moved);
  return list.map((t, i) => ({ ...t, stepOrder: i })).filter((t, i) => (touches[i]?.id !== t.id) || touches[i]?.stepOrder !== i);
}

/**
 * The rows to write to put a new touch just before the close. Returns
 * `{ insert, moves }` — the new row, and the rows whose place changes.
 */
export function touchInsertPlan(touches = [], newTouch) {
  const list = [...(touches || [])];
  const closeAt = list.findIndex((t) => t.touchRole === CLOSE_ROLE);
  const at = closeAt < 0 ? list.length : closeAt;
  list.splice(at, 0, newTouch);
  const renumbered = list.map((t, i) => ({ ...t, stepOrder: i }));
  return {
    insert: renumbered[at],
    moves: renumbered.filter((t, i) => i !== at && t.stepOrder !== (touches.find((x) => x.id === t.id)?.stepOrder)),
  };
}


/* ── What's written, and how much ────────────────────────────────────────────
   Counted off the RAW stored values. No fallback to a role label: reporting
   words on a touch that is completely empty is exactly the state these numbers
   exist to reveal. */

export function touchWords(touch) {
  const text = `${touch?.subject || ""} ${touch?.body || ""}`.trim();
  return text ? text.split(/\s+/).length : 0;
}

/** Characters in the MESSAGE only. The subject is counted in words and never
 *  against a platform limit — no platform with a subject line has one. */
export function touchChars(touch) {
  return String(touch?.body || "").length;
}

export function sequenceWords(touches = []) {
  return (touches || []).reduce((n, t) => n + touchWords(t), 0);
}

/** True when the message has run past its channel's guidance. Always false for
 *  a channel with no limit, so callers don't have to special-case email. */
export function overLimit(touch) {
  const { limit } = channelDef(touch?.channel);
  return !!limit && touchChars(touch) > limit;
}

/* ── State, from ONE helper ──────────────────────────────────────────────────
   Rule 6 of the screen pattern: read from one place so two lists can never
   disagree about the same record.

   DONE IS HER MARK ON THE SEQUENCE, not on each touch — the rule Sites and
   Courses have followed since 2026-09-15 ("mark the whole page done, not each
   section"), applied here with the redesign. A touch is written or it is not.
   (Her mockup draws "Locked" per touch; she answered that for every tool on
   2026-09-16: Done, at the record.) `touches.status` stays on the rows and is
   no longer read — none was ever set to done.

   "Done" rather than "Locked": a lock in this app is her word on Accelerator
   content, and one word should not mean two things. */
export const TOUCH_STATE_LABEL = { done: "Done", draft: "Draft", empty: "Not written" };

export function touchState(touch) {
  return touchWords(touch) === 0 ? "empty" : "draft";
}

/**
 * The sequence's state: Done when she has marked it, otherwise Draft once any
 * touch has words, otherwise empty. The same three words, in the same order,
 * as `pageState` and `moduleStatus`.
 */
export function sequenceState(sequence, touches = []) {
  if (sequence?.status === "done") return "done";
  return (touches || []).some((t) => touchWords(t) > 0) ? "draft" : "empty";
}

/** The first line of the sequence, for the table's preview column. Its opening
 *  touch specifically — the words a stranger actually reads first. */
export function openingLine(touches = []) {
  const opener = (touches || []).find((t) => t.touchRole === "opening") || (touches || [])[0];
  return String(opener?.body || "").trim();
}

/** What to call a sequence in a list. Named for what it is, so a delete confirm
 *  and a rail row can't word an untitled one differently. */
export function sequenceLabel(seq) {
  return String(seq?.name || "").trim() || "Untitled sequence";
}

/* ── Writing a touch with Brill ──────────────────────────────────────────────
   The Outreach half of the shared drafting chat (momentum/drafter.jsx). Same
   machinery as a website section and a course block: Brill answers under
   labelled lines inside delimiters, and nothing is read out of her prose.

   THE FIELD LIST LIVES HERE, ONCE. The touch's editor, the parser and the lines
   Brill is sent all read `touchKindOf`, so a field changed here changes all
   three — api/_lib/prompts.js carries no copy of it. */

export const TOUCH_BLOCK = /<<<TOUCH_DRAFTED>>>([\s\S]*?)<<<TOUCH_END>>>/;

/** The fields a touch is made of. The subject line exists only on a channel
 *  that has one. */
export function touchKindOf(touch) {
  const channel = channelDef(touch?.channel);
  return {
    id: touch?.touchRole || "opening",
    label: touchLabel(touch),
    hint: roleDef(touch?.touchRole).empty.replace(/\.$/, ""),
    fields: [
      ...(channel.subject ? [{ key: "subject", block: "SUBJECT", label: "Subject line", type: "line", guide: "what makes them open it" }] : []),
      { key: "body", block: "MESSAGE", label: "Message", type: "prose", guide: roleDef(touch?.touchRole).label.toLowerCase() },
    ],
  };
}

/** The labels Brill answers under, with the length that matters for THIS
 *  touch's channel. */
export function touchSpecLines(touch) {
  const channel = channelDef(touch?.channel);
  const lines = [];
  if (channel.subject) lines.push("SUBJECT: the subject line — short, specific, nothing that reads like marketing");
  const length = !channel.id
    ? "no channel chosen yet, so keep it short enough for a direct message (under 1000 characters)"
    : channel.limit
      ? `${channel.label}: stay under ${channel.limit} characters${channel.id === "text" ? " — that is one text" : ""}`
      : `${channel.label}: no hard limit, but a few short paragraphs at most`;
  lines.push(`MESSAGE: the message itself — ${length}`);
  return lines;
}

/**
 * What Brill is told about the touch she is writing, sent as its own leading
 * turn: the sequence (what it is for, its goal, the offer it walks toward), this
 * touch's role and channel, the exact labels, what the touch already says, and
 * the other two touches — so the three read as one arc.
 */
export function buildTouchContext({ sequence, touch, touches = [], offers = [] }) {
  const type = sequenceTypeDef(sequence?.type);
  const offerOf = (id) => (offers || []).find((o) => o.id === id) || null;
  const seqOffer = offerOf(sequence?.offerId);
  const touchOffer = offerOf(touch?.offerId);
  const channel = channelDef(touch?.channel);
  const others = (touches || []).filter((t) => t.id !== touch?.id);
  const role = roleDef(touch?.touchRole);

  return [
    "[TOUCH_CONTEXT — read only, do not reference as a conversation]",
    `Sequence: ${sequenceLabel(sequence)}`,
    `What it is for: ${type ? `${type.label} — ${type.hint}` : "not set yet"}`,
    stageLine(sequence, offers),
    `Goal: ${String(sequence?.goal || "").trim() || "not set"}`,
    `Offer the sequence walks toward: ${seqOffer ? offerLine(seqOffer) : "none connected"}`,
    "",
    `This touch: ${touchLabel(touch)}${touch?.of ? ` of ${touch.of}` : ""} — ${role.empty}`,
    `Channel: ${channel.id ? channel.label : "not chosen yet"}${channel.subject ? " (has a subject line)" : ""}`,
    /* With nothing connected, the role's cta ("The soft mention lives here")
       used to follow "none" — and scripts/try-voice.js watched Brill invent a
       freebie to fill it, down to "about twenty minutes, three questions"
       (2026-09-23). No offer means no offer: say so instead. */
    touchOffer
      ? `Offer to mention in this touch: ${offerLine(touchOffer)} — ${role.cta}`
      : "Offer to mention in this touch: none connected. Name no offer, freebie or resource and give no detail of one — if the message wants one, leave a [bracketed placeholder] and tell her what goes there.",
    "",
    "Fields to fill, one per line, using EXACTLY these labels:",
    ...touchSpecLines(touch),
    "",
    "What this touch says now:",
    ...(channel.subject ? [`SUBJECT: ${String(touch?.subject || "").trim() || "(empty)"}`] : []),
    `MESSAGE: ${String(touch?.body || "").trim() || "(empty)"}`,
    "",
    "The other touches in this sequence, in order, for fit — do not rewrite them:",
    ...(others.length
      ? others.map((t) => `${touchLabel(t)} (${channelDef(t.channel).id ? channelDef(t.channel).label : "no channel yet"}): ${String(t.body || "").trim() || "(not written)"}`)
      : ["(none)"]),
  ].join("\n");
}

export function parseTouchBlock(text, touch) {
  return parseLabelledBlock(text, touchKindOf(touch).fields, TOUCH_BLOCK);
}

export function stripTouchBlock(text) {
  return stripLabelledBlock(text, TOUCH_BLOCK);
}

export function applyTouchBlock(content, block, touch) {
  return applyLabelledBlock(content, block, touchKindOf(touch).fields);
}


/* ── Writing the WHOLE sequence with Brill ───────────────────────────────────
   The record scope of the shared drafter (2026-09-16; decision 6 made this
   Outreach's default door). One reply can fill several touches, so each
   touch's fields get labels of their own — TOUCH_THREE_MESSAGE. Words, not
   digits: the shared label reader only accepts capitals and underscores, and
   widening it to digits would let a line like "Q3: …" inside a website answer
   end the field early. Same <<<TOUCH_DRAFTED>>> block as one touch. */

/* The place words (ONE, TWO…) are shared with the website's whole page. */
const ordinal = ordinalWord;

/** Every touch's fields, each with a label unique to its place. `key` is
 *  `${touchId}::${field}`, which is how a parsed answer finds its touch. */
export function sequenceFields(touches = []) {
  const out = [];
  (touches || []).forEach((t, i) => {
    for (const f of touchKindOf(t).fields) {
      out.push({ ...f, key: `${t.id}::${f.key}`, block: `TOUCH_${ordinal(i)}_${f.block}`, touchId: t.id, field: f.key });
    }
  });
  return out;
}

export function buildSequenceContext({ sequence, touches = [], offers = [] }) {
  const type = sequenceTypeDef(sequence?.type);
  const offerOf = (id) => (offers || []).find((o) => o.id === id) || null;
  const seqOffer = offerOf(sequence?.offerId);
  const fields = sequenceFields(touches);
  const lines = [
    "[SEQUENCE_CONTEXT — read only, do not reference as a conversation]",
    "Scope: THE WHOLE SEQUENCE. Write as many touches as you genuinely have words for, each under its own labels, all inside ONE block.",
    `Sequence: ${sequenceLabel(sequence)}`,
    `What it is for: ${type ? `${type.label} — ${type.hint}` : "not set yet"}`,
    stageLine(sequence, offers),
    `Goal: ${String(sequence?.goal || "").trim() || "not set"}`,
    `Offer the sequence walks toward: ${seqOffer ? offerLine(seqOffer) : "none connected"}`,
    `Touches: ${(touches || []).length}`,
    "",
  ];
  (touches || []).forEach((t, i) => {
    const channel = channelDef(t.channel);
    const role = roleDef(t.touchRole);
    const touchOffer = offerOf(t.offerId);
    lines.push(`— Touch ${i + 1} of ${touches.length}: ${role.label} — ${role.empty}`);
    lines.push(`  Channel: ${channel.id ? channel.label : "not chosen yet"}. ${touchSpecLines(t).map((l) => l.replace(/^[A-Z]+: /, "")).join(" ")}`);
    lines.push(`  Offer to mention: ${touchOffer ? offerLine(touchOffer) : "none"} — ${role.cta}`);
    for (const f of fields.filter((x) => x.touchId === t.id)) {
      lines.push(`  ${f.block}: ${String(t[f.field] || "").trim() || "(empty)"}`);
    }
    lines.push("");
  });
  lines.push("Labels to answer under (omit any touch you are not changing):");
  lines.push(...fields.map((f) => f.block));
  return lines.join("\n");
}

/** { [touchId]: { subject?, body? } } for the touches Brill wrote, or null. */
export function parseSequenceBlock(text, touches = []) {
  const fields = sequenceFields(touches);
  const flat = parseLabelledBlock(text, fields, TOUCH_BLOCK);
  if (!flat) return null;
  const out = {};
  for (const f of fields) {
    if (flat[f.key] === undefined) continue;
    (out[f.touchId] = out[f.touchId] || {})[f.field] = flat[f.key];
  }
  return Object.keys(out).length ? out : null;
}


/* ── The store ───────────────────────────────────────────────────────────────
   Two tables, parent and child, both with their own `clerk_user_id` — unlike
   `website_sections`, which is owned through its page. That is 0001's decision,
   not ours: a message can stand alone with a null `sequence_id`, so it needs its
   own ownership column for RLS to have anything to filter on.

   `delayDays` is deliberately NOT in the message fields. Her note on the mockup:
   "no cadence bar, no send times — this reads like one woman reaching out to
   another, not a sequence running itself." The column keeps its default for the
   day GHL connects. */

/* `platform` is gone from the list: a channel belongs to each touch now. The
   column stays in the table (0009, not null, defaulted) and nothing writes it. */
export const SEQUENCE_FIELDS = ["name", "type", "goal", "offerId", "ctaId", "status", "sortOrder"];

export const TOUCH_FIELDS = [
  "sequenceId", "touchRole", "stepOrder", "channel", "subject", "body", "offerId", "status",
  // Provenance, the same three every seeded record carries. Null on every touch
  // today — a sequence is written from empty. They are here so the drafting pass
  // has somewhere to record what it wrote from, rather than discovering the gap
  // later as a column that silently never writes.
  "source", "sourceText", "sourceHash",
];

const useSequenceRecords = createRecordStore({
  table: "outbound_sequences",
  fields: SEQUENCE_FIELDS,
  idPrefix: "seq",
  orderBy: "sort_order",
  ascending: true,
});

const useTouchRecords = createRecordStore({
  table: "outbound_messages",
  fields: TOUCH_FIELDS,
  idPrefix: "otm",
  orderBy: "step_order",
  ascending: true,
});

export function useOutreach() {
  const {
    rows: sequences, loading: seqLoading, error: seqError,
    save: saveSequence, remove: removeSequence, clearError: clearSeqError, newId: newSequenceId,
  } = useSequenceRecords();

  const {
    rows: touchRows, loading: touchesLoading, error: touchError,
    save: saveTouch, remove: removeTouch, clearError: clearTouchError, newId: newTouchId,
  } = useTouchRecords();

  /* Every touch of every sequence in one query rather than one per sequence —
     the table shows a word count and a state per row, so fetching per sequence
     would be a request inside a render loop. */
  const bySequence = useMemo(() => {
    const map = new Map();
    for (const t of touchRows || []) {
      const list = map.get(t.sequenceId) || [];
      list.push(t);
      map.set(t.sequenceId, list);
    }
    /* Sorted here rather than trusting the query: a row saved during this session
       is put at the HEAD of `rows` by the store, which is right for a
       newest-first list and wrong for an ordered one. */
    for (const [id, list] of map.entries()) {
      list.sort((a, b) => (a.stepOrder ?? 0) - (b.stepOrder ?? 0));
      /* Its place in the sequence, for "Touch 4 of 7". Not a stored field —
         the store only ever writes the fields it names, so these ride along
         harmlessly on a save. */
      map.set(id, list.map((t, i) => ({ ...t, position: i + 1, of: list.length })));
    }
    return map;
  }, [touchRows]);

  const touchesFor = useCallback((sequenceId) => bySequence.get(sequenceId) || [], [bySequence]);

  /** Make sure a sequence has its two ends — an opening and a close — and, on
   *  a brand-new sequence, the value-give between them.
   *
   *  Called on create and on open. On open ONLY the ends are required: she may
   *  have removed every middle touch on purpose, and putting one back each time
   *  she opened the sequence would be undoing her. A create whose later writes
   *  failed is repaired by the same call.
   *
   *  `channel` starts EMPTY — chosen per touch. The column is `not null` from
   *  0001, and an empty string is the honest "not chosen yet". */
  const ensureTouches = useCallback(async (sequence, { fresh = false } = {}) => {
    if (!sequence?.id) return [];
    const have = touchesFor(sequence.id);
    const roles = new Set(have.map((t) => t.touchRole));
    const want = fresh ? STARTING_ROLES : [OPENING_ROLE, CLOSE_ROLE];
    const made = [];
    for (const role of want) {
      if (roles.has(role)) continue;
      /* The opening goes first, the close last, anything else before the close. */
      const stepOrder = role === OPENING_ROLE ? -1 : role === CLOSE_ROLE ? 1000 : want.indexOf(role);
      // Sequential, not parallel: recordStore.save() replaces rows in local state
      // one at a time, and racing writes can interleave onto a stale list.
      const saved = await saveTouch({
        id: newTouchId(),
        sequenceId: sequence.id,
        touchRole: role,
        stepOrder,
        channel: "",
        subject: "",
        body: "",
        status: "draft",
      });
      if (saved) made.push(saved);
    }
    return made;
  }, [touchesFor, saveTouch, newTouchId]);

  /** A new touch just before the close. Writes the new row, then renumbers the
   *  ones after it. Returns the new touch, or null. */
  const addTouch = useCallback(async (sequence, role = "value") => {
    if (!sequence?.id) return null;
    const draft = {
      id: newTouchId(), sequenceId: sequence.id, touchRole: role,
      channel: "", subject: "", body: "", status: "draft",
    };
    const { insert, moves } = touchInsertPlan(touchesFor(sequence.id), draft);
    const saved = await saveTouch(insert);
    if (!saved) return null;
    for (const m of moves) await saveTouch(m);
    return saved;
  }, [touchesFor, saveTouch, newTouchId]);

  /** Remove a middle touch. The opening and the close stay — they are the arc. */
  const removeTouchRow = useCallback(async (touch) => {
    if (!touch?.id || !isMiddleTouch(touch)) return false;
    return removeTouch(touch.id);
  }, [removeTouch]);

  /** Drag a middle touch to a new place between the ends. */
  const reorderTouches = useCallback(async (sequenceId, from, to) => {
    let ok = true;
    for (const row of touchReorderWrites(touchesFor(sequenceId), from, to)) {
      if (!(await saveTouch(row))) ok = false;
    }
    return ok;
  }, [touchesFor, saveTouch]);

  /** A new sequence of a TYPE, at the bottom of the list, with its three
   *  touches already in it. Returns the sequence, or null if the write failed. */
  const addSequence = useCallback(async (type) => {
    const last = sequences.length
      ? Math.max(...sequences.map((s) => s.sortOrder ?? 0))
      : -1;
    const saved = await saveSequence({
      id: newSequenceId(),
      name: "",
      type: type || null,
      goal: sequenceTypeDef(type)?.goal || "",
      status: "draft",
      sortOrder: last + 1,
    });
    if (saved) await ensureTouches(saved, { fresh: true });
    return saved;
  }, [sequences, saveSequence, newSequenceId, ensureTouches]);

  /** Delete a sequence and everything in it.
   *
   *  `outbound_messages.sequence_id` is `on delete set null`, NOT cascade — 0001
   *  chose that so a standalone message survives. Which means deleting a
   *  sequence would otherwise leave three orphan rows loading forever with no
   *  screen able to reach them, so the touches go first and explicitly. */
  const deleteSequence = useCallback(async (id) => {
    for (const t of touchesFor(id)) {
      if (!(await removeTouch(t.id))) return false;
    }
    return removeSequence(id);
  }, [touchesFor, removeTouch, removeSequence]);

  /** Move a sequence to an arbitrary position, which is what a drag produces.
   *  Renumbers the whole list — see utils/reorder.js. */
  const reorderSequences = useCallback(async (from, to) => {
    let ok = true;
    for (const row of reorderWrites(sequences, from, to)) {
      if (!(await saveSequence(row))) ok = false;
    }
    return ok;
  }, [sequences, saveSequence]);


  return {
    sequences,
    loading: seqLoading || touchesLoading,
    error: seqError || touchError,
    clearError: () => { clearSeqError(); clearTouchError(); },
    touchesFor,
    saveSequence, deleteSequence, addSequence, reorderSequences,
    saveTouch, ensureTouches, addTouch, removeTouch: removeTouchRow, reorderTouches,
  };
}

/** The table's bands: each stage that has sequences, in stage order, then any
 *  with no stage yet (no type and no linked offer). Order within a band is
 *  the sequences' own. */
export function stageBands(sequences = [], offers = []) {
  const bands = SEQUENCE_STAGES
    .map((stage) => ({ key: stage, label: rungLabel(stage), rows: sequences.filter((s) => sequenceStage(s, offers) === stage) }))
    .filter((b) => b.rows.length);
  const none = sequences.filter((s) => !sequenceStage(s, offers));
  if (none.length) bands.push({ key: "none", label: "No stage yet", rows: none });
  return bands;
}
